import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getClassHeatmap, getEnterpriseHeatmap, readableApiError, HEATMAP_SORTS,
} from '../../api/dashboard';
import './dashboard.css';

/**
 * 4.2.4 个体差异分布。
 *
 * 数据复用 heatmap endpoint（每人各维度分），不需要新接口 —— 设计文档第 5 节
 * "共用底盘"原则：相同数据用一份接口。
 *
 * 渲染：每维度一条横向散点图（学员每人一个点），用来发现"两极分化"维度。
 * 排序：默认按 spread (max-min) 降序，最分化的维度排前面。
 */
export default function ClassDistributionPage({ scope = 'class' }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const isEnt = scope === 'enterprise';
  const basePath = isEnt ? `/dashboard/enterprises/${id}` : `/dashboard/classes/${id}`;

  useEffect(() => {
    setData(null); setError(null);
    (isEnt
      ? getEnterpriseHeatmap(id, { sort: HEATMAP_SORTS.SCORE_DESC })
      : getClassHeatmap(id, { sort: HEATMAP_SORTS.SCORE_DESC }))
      .then(setData)
      .catch((err) => setError(readableApiError(err)));
  }, [id, isEnt]);

  if (error) return <div className="aiq-dash dash-page"><div className="dash-card dash-card--error">{error}</div></div>;
  if (!data) return <div className="aiq-dash dash-page"><div className="dash-card">加载中...</div></div>;

  const stats = data.dimensions.map((d, i) => {
    const arr = data.rows.map((r) => r.scores[i]).filter((s) => s != null);
    if (arr.length === 0) {
      return { dim: d, scores: [], mean: null, min: null, max: null, spread: 0 };
    }
    const sum = arr.reduce((a, b) => a + b, 0);
    return {
      dim: d,
      scores: arr,
      mean: +(sum / arr.length).toFixed(1),
      min: Math.min(...arr),
      max: Math.max(...arr),
      spread: Math.max(...arr) - Math.min(...arr),
    };
  });

  // 按 spread 降序，最分化的维度排前面；空数据沉底
  stats.sort((a, b) => {
    if (a.scores.length === 0) return 1;
    if (b.scores.length === 0) return -1;
    return b.spread - a.spread;
  });

  return (
    <div className="aiq-dash dash-page">
      <div className="dash-head">
        <h1 className="dash-h1">个体差异分布</h1>
        <p className="dash-sub">每维度的最低-平均-最高分布。差距越大表示团队在该维度两极分化越严重。</p>
      </div>

      <Link to={basePath} className="dash-back">← 返回总览</Link>

      {data.rows.length === 0 ? (
        <div className="dash-card"><div className="dash-mut">暂无已完成测评的学员</div></div>
      ) : (
        <div className="dash-card">
          <div className="dash-sec"><h2>按"分化程度"降序</h2><span className="dash-meta">差距越大越分化</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {stats.map((s) => (
              <div key={s.dim.code}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>{s.dim.name}</span>
                  {s.scores.length === 0 ? (
                    <span className="dash-mut">无数据</span>
                  ) : (
                    <span className="dash-mut">
                      min {s.min} · mean {s.mean} · max {s.max}
                      · 差距 <b style={{ color: 'var(--da-warn)' }}>{s.spread}</b>
                    </span>
                  )}
                </div>
                <div className="dash-spread">
                  {s.scores.length > 0 && (
                    <>
                      {/* 区间条 */}
                      <div className="dash-spread__range" style={{
                        left: `${s.min}%`, width: `${Math.max(s.max - s.min, 1)}%`,
                      }} />
                      {/* 学员点 */}
                      {s.scores.map((sc, idx) => (
                        <div key={idx} className="dash-spread__dot" title={`学员: ${sc}`}
                             style={{ left: `calc(${sc}% - 4px)` }} />
                      ))}
                      {/* 均值线 */}
                      <div className="dash-spread__mean" style={{ left: `calc(${s.mean}% - 1px)` }} />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="dash-mut" style={{ marginTop: 12 }}>紫点 = 学员；灰条 = min~max 区间；红线 = 均值。</div>
        </div>
      )}
    </div>
  );
}
